import {
  FuzzySearchConfig,
  RRFConfig,
  SEARCH_RESULT_CATEGORY,
  SearchableItemPhase,
  SemanticSearchConfig,
  SmartSearchConfig,
  SmartSearchMode,
} from './types'

// Re-export the config type for consumers who import from constants
export type { SmartSearchConfig } from './types'

/**
 * Default RRF k constant.
 * Industry standard value (k=60) from the original RRF paper.
 * Only change with careful consideration and thorough testing.
 */
export const DEFAULT_RRF_K = 60

/** Default RRF threshold for filtering low-confidence results */
export const DEFAULT_RRF_THRESHOLD = 0.0143

/** Default semantic API timeout in milliseconds */
export const DEFAULT_SEMANTIC_TIMEOUT_MS = 10000

/** All valid phases for searchable items */
export const ALL_SEARCHABLE_ITEM_PHASES: SearchableItemPhase[] = ['P0', 'P1', 'P2']

/** Minimum query length for search */
export const DEFAULT_SEARCH_MIN_QUERY_LENGTH = 3

/**
 * Common stop words to filter out from search queries.
 * These words are so common that they add noise to search results.
 */
export const DEFAULT_SEARCH_QUERY_STOP_WORDS = new Set([
  // Articles
  'a',
  'an',
  'the',
  // Prepositions
  'in',
  'on',
  'at',
  'to',
  'for',
  'of',
  'with',
  'by',
  'from',
  'as',
  'into',
  'through',
  'during',
  'before',
  'after',
  // Conjunctions
  'and',
  'or',
  'but',
  'nor',
  'so',
  'yet',
  // Pronouns
  'i',
  'me',
  'my',
  'myself',
  'we',
  'our',
  'ours',
  'ourselves',
  'you',
  'your',
  'yours',
  'yourself',
  'yourselves',
  'he',
  'him',
  'his',
  'himself',
  'she',
  'her',
  'hers',
  'herself',
  'it',
  'its',
  'itself',
  'they',
  'them',
  'their',
  'theirs',
  'themselves',
  'what',
  'which',
  'who',
  'whom',
  'this',
  'that',
  'these',
  'those',
  // Common verbs
  'am',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'have',
  'has',
  'had',
  'having',
  'do',
  'does',
  'did',
  'doing',
  'would',
  'should',
  'could',
  'ought',
  'cannot',
  'can',
  'will',
  'shall',
  'might',
  'must',
  // Common adverbs
  'just',
  'very',
  'really',
  'quite',
  'rather',
  'too',
  'also',
  'only',
  'even',
  'still',
  'already',
  'always',
  'never',
  // Other common words
  'about',
  'get',
  'got',
  'getting',
  'want',
  'need',
  'like',
  'some',
  'any',
  'no',
  'not',
  'more',
  'most',
  'other',
  'such',
  'own',
  'same',
  'than',
  'then',
  'now',
  'here',
  'there',
  'when',
  'where',
  'why',
  'how',
  'all',
  'each',
  'few',
  'both',
  'any',
  'most',
  'other',
  'some',
  'such',
  'only',
  'over',
  'because',
  'if',
  'while',
  // Contractions (common forms)
  "i'm",
  "i've",
  "i'll",
  "i'd",
  "you're",
  "you've",
  "you'll",
  "you'd",
  "he's",
  "he'll",
  "he'd",
  "she's",
  "she'll",
  "she'd",
  "it's",
  "it'll",
  "we're",
  "we've",
  "we'll",
  "we'd",
  "they're",
  "they've",
  "they'll",
  "they'd",
  "isn't",
  "aren't",
  "wasn't",
  "weren't",
  "hasn't",
  "haven't",
  "hadn't",
  "doesn't",
  "don't",
  "didn't",
  "won't",
  "wouldn't",
  "shan't",
  "shouldn't",
  "can't",
  "couldn't",
  "mustn't",
  "let's",
  "that's",
  "who's",
  "what's",
  "here's",
  "there's",
  "when's",
  "where's",
  "why's",
  "how's",
  'trouble',
  'help',
])

/** Default fuzzy search configuration */
export const DEFAULT_FUZZY_CONFIG: FuzzySearchConfig = {
  standardThreshold: 0.249,
  strictThreshold: 0.15,
  displayNameWeight: 3,
  keywordsWeight: 1,
}

/**
 * Semantic score-gap ("elbow") cutoff. Trims the weak tail once the similarity score drops
 * by more than this from the previous result. Applied to the similarity-ranked semantic
 * results before fusion (see applyScoreGapCutoff). Overridable via LaunchDarkly/DevTools.
 */
const DEFAULT_SCORE_GAP_CUTOFF = 0.08

/**
 * Minimum raw semantic score for the care-intent catch-all ("Search for care" /
 * "Child & teen care"). Deliberately below similarityThreshold (0.52 in the live config):
 * the catch-all only fires when other care results are present, so a lower bar is safe and
 * captures natural-language care queries ("burnout at work", "going through a divorce")
 * where the triage item scores ~0.50 but doesn't rank organically.
 */
const DEFAULT_CATCH_ALL_SCORE_THRESHOLD = 0.48

/** Default semantic search configuration */
export const DEFAULT_SEMANTIC_CONFIG: SemanticSearchConfig = {
  similarityThreshold: 0.5,
  strictSimilarityThreshold: 0.85,
  similarityBucketSize: 0.05,
  keywordBoostThreshold: 0.5,
  minimumWordLength: 2,
  ignoreStopWords: true,
  additionalStopWords: [...DEFAULT_SEARCH_QUERY_STOP_WORDS],
  requireKeywordMatch: false,
  skipRanking: false,
  includeDisplaynameMatches: false,
  timeoutMs: DEFAULT_SEMANTIC_TIMEOUT_MS,
  embeddingBackend: 'sagemaker',
  embeddingModel: 'mxbai-embed-cls-pooling',
  scoreGapCutoff: DEFAULT_SCORE_GAP_CUTOFF,
  catchAllScoreThreshold: DEFAULT_CATCH_ALL_SCORE_THRESHOLD,
  stopWordQueryKeywordRankBonus: 2,
}

/**
 * Words too generic to carry relevance signal on their own.
 * Verbatim mirror of the GenAIService semantic-search GENERIC_WORDS denylist — the same
 * list that gates displayName promotion server-side. Keep in sync with the backend.
 */
export const GENERIC_QUERY_WORDS: string[] = [
  // Broad clinical/medical terms that appear across many unrelated items
  'disorder',
  'disorders',
  'treatment',
  'counseling',
  'therapy',
  'support',
  'health',
  'mental',
  'behavioral',
  'clinical',
  'wellness',
  'recovery',
  'care',
  // Generic descriptors common across items
  'issues',
  'problems',
  'challenges',
  'services',
  'service',
  'program',
  'programs',
  'management',
  'options',
  'resources',
  'condition',
  'conditions',
  'provider',
  'sessions',
  'session',
  // Broad action/context words
  'use',
]

/**
 * fuzzyBucketSize used for generic-only queries: larger than any possible Fuse score
 * spread (scores are ≤ the 0.249 fuzzy threshold), so every fuzzy result shares rank 1
 * and the RRF merge order follows the semantic ranking.
 */
export const GENERIC_QUERY_FUZZY_BUCKET_COLLAPSE = 1

/**
 * Semantic-confidence escape hatch for the generic-query evidence demotion: on a
 * generic-only query, a zero-evidence item keeps its rank when its semantic score clears
 * this bar. Validated via golden-set measurement — observed zero-evidence
 * noise sits at ~0.52–0.56, so 0.62 clears it with margin. LaunchDarkly-overridable.
 */
const DEFAULT_GENERIC_DEMOTION_SCORE_THRESHOLD = 0.62

/** Default penalty for fuzzy-only results (10% penalty) */
export const DEFAULT_FUZZY_ONLY_PENALTY = 0.9

/** Default fuzzy bucket size for score-aware RRF (items within this score range share a rank) */
const DEFAULT_FUZZY_BUCKET_SIZE = 0.005

/** Default rescue semantic margin (how far below the dual-list anchor a near-miss can be rescued) */
const DEFAULT_RESCUE_SEMANTIC_MARGIN = 0.04

/** Default rescue fuzzy max score (only near-exact fuzzy matches are rescue-eligible) */
const DEFAULT_RESCUE_FUZZY_MAX_SCORE = 0.01

/** Default RRF configuration */
export const DEFAULT_RRF_CONFIG: RRFConfig = {
  k: DEFAULT_RRF_K,
  threshold: DEFAULT_RRF_THRESHOLD,
  fuzzyOnlyPenalty: DEFAULT_FUZZY_ONLY_PENALTY,
  fuzzyBucketSize: DEFAULT_FUZZY_BUCKET_SIZE,
  rescueSemanticMargin: DEFAULT_RESCUE_SEMANTIC_MARGIN,
  rescueFuzzyMaxScore: DEFAULT_RESCUE_FUZZY_MAX_SCORE,
  genericQueryWords: GENERIC_QUERY_WORDS,
  genericDemotionScoreThreshold: DEFAULT_GENERIC_DEMOTION_SCORE_THRESHOLD,
}

/** Default smart search configuration */
export const DEFAULT_SMART_SEARCH_CONFIG: SmartSearchConfig = {
  mode: SmartSearchMode.FUZZY,
  fuzzy: DEFAULT_FUZZY_CONFIG,
  semantic: DEFAULT_SEMANTIC_CONFIG,
  rrf: DEFAULT_RRF_CONFIG,
  minQueryLength: DEFAULT_SEARCH_MIN_QUERY_LENGTH,
  debounceMs: 500,
  scoreDiffThreshold: 0,
  maxResults: 10,
  enabledPhases: ['P0', 'P1'],
  categoryMaxResults: {
    [SEARCH_RESULT_CATEGORY.FAQS]: 3,
  },
}

/**
 * Value passed as the `referrer` query param on Essentials URLs opened from Smart Search.
 * Goodness reads it to set the `referrer` prop on its page-view events. Ticket: CEA-3703
 */
export const SMART_SEARCH_ESSENTIALS_REFERRER = 'Smart Search'
